import type { ReactNode } from 'react';
import { Database, Hourglass } from 'lucide-react';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
import { EvidenceMark } from '../ui/EvidenceMark';
import { sentence } from '../lib/format';
import type { TrackData } from '../data/useTrackData';
import { notConnected } from '../data-source/refusals';
import { SkeletonRegion, SkeletonText } from '../ui/Skeleton';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  notConnectedTitle: {
    id: 'common.parts.notConnectedTitle',
    defaultMessage: 'База района этого пока не отдаёт',
    description: 'Заголовок пустого состояния блока: данных этого блока в базе района пока нет',
  },
  notConnectedReason: {
    id: 'common.parts.notConnectedReason',
    defaultMessage: '{what}: {reason}.',
    description: 'Пояснение пустого блока; {what} — что не показано (с заглавной буквы, «Статус карантина»), {reason} — причина без точки',
  },
  generatingTitle: {
    id: 'common.parts.generatingTitle',
    defaultMessage: 'Данные ещё генерируются',
    description: 'Заголовок пустого состояния блока демонстрационных данных: файл ещё не готов',
  },
  generatingReason: {
    id: 'common.parts.generatingReason',
    defaultMessage: '{what}. {reason}.',
    description: 'Пояснение пустого блока; {what} — что не показано (с заглавной буквы), {reason} — причина',
  },
  noReason: {
    id: 'common.parts.noReason',
    defaultMessage: 'причина не названа',
    description: 'Причина пустого блока, когда её никто не назвал (строчная фраза)',
  },
  stub: {
    id: 'common.parts.stub',
    defaultMessage: 'Трек {track} этот файл не отдал — показана заглушка',
    description: 'Метка «Демо» над блоком: выбранный набор данных файл не отдал, показаны заглушечные данные; {track} — буква набора (A или B)',
  },
  placeholderTitle: {
    id: 'common.parts.placeholderTitle',
    defaultMessage: 'Блок ещё не наполнен',
    description: 'Заголовок пустого состояния блока-заготовки (раздел ещё не готов)',
  },
});


/** «статус карантина ждёт quarantine.json» → «статус карантина»: имена файлов — не для экрана. */
function withoutFiles(what: string): string {
  return what.replace(/\s*(ждёт|ждут)(\s+файлы)?\s+\S*\.json\S*/gi, '').trim();
}

/**
 * Единственное место, где решается, что делать с отсутствующим файлом.
 * Отсутствие фикстуры — состояние экрана, а не ошибка приложения.
 */
export function DataGate<T>({
  res,
  what,
  children,
  skeleton,
}: {
  res: TrackData<T>;
  /** Что именно не удалось показать — словами. */
  what: string;
  children: (data: T) => ReactNode;
  /** Скелетон в размер готового блока (6al.49); по умолчанию — строки текста. */
  skeleton?: ReactNode;
}) {
  const t = useT();
  if (res.state === 'loading') {
    // «Нет данных» до конца загрузки не говорим: скелетон, а не подпись (6al.49).
    return <>{skeleton ?? <SkeletonRegion label={withoutFiles(what)} minHeight={96}><SkeletonText lines={4} /></SkeletonRegion>}</>;
  }

  if (res.state === 'missing' && res.reason === notConnected()) {
    return (
      <EmptyState
        compact
        icon={Database}
        title={t(m.notConnectedTitle)}
        reason={t(m.notConnectedReason, { what: sentence(withoutFiles(what)), reason: notConnected() })}
      />
    );
  }

  if (res.state === 'missing' || res.data === null) {
    return (
      <EmptyState
        compact
        icon={Database}
        title={t(m.generatingTitle)}
        reason={t(m.generatingReason, { what: sentence(what), reason: sentence(res.reason ?? t(m.noReason)) })}
      />
    );
  }

  // Подмена трека заглушкой не должна выглядеть как факт среза.
  if (res.source === 'stub') {
    return (
      <>
        <div style={{ marginBottom: 'var(--sp-2)' }}>
          <EvidenceMark
            kind="demo"
            label={t(m.stub, { track: res.track })}
          />
        </div>
        {children(res.data)}
      </>
    );
  }

  return <>{children(res.data)}</>;
}

/** Место под блок, который наполняет следующая волна экранов. */
export function Placeholder({
  title,
  what,
  aside,
}: {
  title: string;
  what: string;
  aside?: ReactNode;
}) {
  const t = useT();
  return (
    <Card title={title} aside={aside}>
      <EmptyState compact icon={Hourglass} title={t(m.placeholderTitle)} reason={what} />
    </Card>
  );
}
